import { Component, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error("Result page crashed:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: "100vh",
          background: "#f5edd8",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "2rem",
          textAlign: "center",
          fontFamily: "'Fraunces', 'Playfair Display', serif",
        }}>
          <p style={{ fontSize: 22, fontWeight: 700, color: "#2c1f14", margin: "0 0 8px" }}>
            we couldn't finish this one
          </p>
          <p style={{ fontSize: 14, color: "#5a4535", margin: "0 0 8px", lineHeight: 1.5 }}>
            Sorry — something went wrong on our end and we can't show you a verdict for this scan.
          </p>
          <p style={{
            fontSize: 12, color: "#5a4535", opacity: 0.85,
            margin: "0 0 20px", fontFamily: "'IBM Plex Mono', monospace",
            letterSpacing: "0.04em",
          }}>
            We didn't use one of your scan credits.
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "12px 22px",
                background: "#2c1f14",
                color: "#f5edd8",
                border: "none",
                cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Try again
            </button>
            <button
              onClick={() => window.location.href = "/home"}
              style={{
                padding: "12px 22px",
                background: "transparent",
                color: "#2c1f14",
                border: "1px solid #2c1f14",
                cursor: "pointer",
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              Go home
            </button>
          </div>
          {this.state.error && (
            <pre style={{
              fontSize: "0.65rem",
              marginTop: "1.5rem",
              color: "#5a4535",
              opacity: 0.8,
              maxWidth: 420,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              textAlign: "left",
              background: "rgba(0,0,0,0.05)",
              padding: "10px 12px",
              borderRadius: 4,
            }}>
              <strong>{this.state.error.message}</strong>
              {"\n\n"}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}
